import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";
import { ShieldCheck, ShieldAlert, ShieldX, ChevronRight, ArrowRight } from "lucide-react";

const TONE = {
  green: { label: "All clear", cls: "bg-emerald-50 text-emerald-800 border-emerald-300", dot: "bg-emerald-500", Icon: ShieldCheck },
  yellow: { label: "Needs attention", cls: "bg-amber-50 text-amber-800 border-amber-300", dot: "bg-amber-400", Icon: ShieldAlert },
  red: { label: "Action required", cls: "bg-rose-50 text-rose-800 border-rose-300", dot: "bg-rose-500", Icon: ShieldX },
};
const CATEGORY = { docs: "Expiring documents", randoms: "Random selections", leave: "Leave returns" };

export function HealthDot({ level, size = "sm", testid }) {
  const t = TONE[level] || TONE.green;
  return <span className={`inline-block rounded-full ${t.dot} ${size === "lg" ? "h-3.5 w-3.5" : "h-2.5 w-2.5"}`} data-testid={testid} aria-label={t.label} />;
}

export function HealthDetailsDialog({ open, onOpenChange, customerId, company }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    if (!open) return;
    setData(null);
    api.get("/health-score", { params: customerId ? { customer_id: customerId } : {} }).then((r) => setData(r.data)).catch((e) => toast.error(formatApiError(e)));
  }, [open, customerId]);
  const t = TONE[data?.level] || TONE.green;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="health-score-dialog">
        <DialogHeader><DialogTitle className="font-heading flex items-center gap-2"><t.Icon className="h-5 w-5" />Compliance health{company ? ` · ${company}` : ""}</DialogTitle></DialogHeader>
        {!data ? <div className="text-sm text-slate-500 py-6">Loading…</div> : (
          <div className="space-y-4">
            <div className={`rounded-md border px-3 py-2 text-sm font-semibold ${t.cls}`} data-testid="health-score-level">
              {t.label} · {data.red} critical · {data.yellow} warning{data.yellow !== 1 ? "s" : ""}
            </div>
            {data.reasons.length === 0 && <p className="text-sm text-slate-600" data-testid="health-score-empty">No expired or expiring documents, no open random selections and no overdue leave returns.</p>}
            {["docs", "randoms", "leave"].map((cat) => {
              const rows = data.reasons.filter((r) => r.category === cat);
              if (!rows.length) return null;
              return (
                <div key={cat} data-testid={`health-group-${cat}`}>
                  <div className="tag-mono text-slate-500 mb-1.5">{CATEGORY[cat].toUpperCase()} · {rows.length}</div>
                  <ul className="divide-y divide-slate-100 border border-slate-200 rounded-md">
                    {rows.map((r, i) => (
                      <li key={i} data-testid={`health-reason-${cat}-${i}`}>
                        {r.href ? (
                          <Link to={r.href} onClick={() => onOpenChange(false)} className="flex items-start gap-2 px-3 py-2 text-sm hover:bg-slate-50 group" data-testid={`health-fix-${cat}-${i}`}>
                            <HealthDot level={r.severity} />
                            <div className="min-w-0 flex-1"><div className="font-medium text-slate-900 truncate">{r.label}</div><div className="text-xs text-slate-600">{r.detail}</div></div>
                            <span className="text-xs font-semibold text-blue-700 whitespace-nowrap inline-flex items-center gap-0.5 opacity-70 group-hover:opacity-100">Fix it <ArrowRight className="h-3 w-3" /></span>
                          </Link>
                        ) : (
                          <div className="flex items-start gap-2 px-3 py-2 text-sm">
                            <HealthDot level={r.severity} />
                            <div className="min-w-0"><div className="font-medium text-slate-900 truncate">{r.label}</div><div className="text-xs text-slate-600">{r.detail}</div></div>
                          </div>
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              );
            })}
            <p className="text-xs text-slate-500">Red = anything expired, an overdue collection or an overdue leave return. Yellow = expiring within 30 days or open random selections. As of {data.as_of}.</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

export function HealthScoreBadge({ customerId, company, summary, compact = false }) {
  const [open, setOpen] = useState(false);
  const [local, setLocal] = useState(summary || null);
  useEffect(() => { if (summary) setLocal(summary); }, [summary]);
  useEffect(() => {
    if (summary) return;
    api.get("/health-score", { params: customerId ? { customer_id: customerId } : {} }).then((r) => setLocal(r.data)).catch(() => {});
  }, [customerId, summary]);
  const t = TONE[local?.level] || TONE.green;
  const testid = compact ? `health-badge-${customerId}` : "health-score-badge";
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} data-testid={testid} title="Compliance health — click for details"
        className={`inline-flex items-center gap-2 rounded-full border font-semibold transition-colors hover:brightness-95 ${t.cls} ${compact ? "px-2 py-0.5 text-xs" : "px-4 py-2 text-sm"}`}>
        <HealthDot level={local?.level} size={compact ? "sm" : "lg"} />
        {local ? (compact ? `${t.label}${local.red || local.yellow ? ` · ${local.red + local.yellow}` : ""}` : `Compliance health: ${t.label}`) : "…"}
        {!compact && <ChevronRight className="h-4 w-4 opacity-60" />}
      </button>
      <HealthDetailsDialog open={open} onOpenChange={setOpen} customerId={customerId} company={company} />
    </>
  );
}
